<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="element-box">
        <CoachingOverviewData :coaching="coachingData"/>
        <Menu :coaching="coachingData"
              :currentUser="piniaCurrentUser"/>

        <form v-if="isLoaded" @submit.prevent="submitForm" novalidate class="mt-4">
          <div class="row mt-3">
            <div class="col-lg-3 col-md-4 col-sm-12 mb-4">
              <BaseCheckbox
                  v-model="formData.selectionInterview"
                  label="Auswahlgespräch"
                  :classFormGroup="'mb-3'"
                  :labelPosition="'right'"/>
              <BaseCheckbox
                  v-model="formData.houseRule"
                  label="Hausordnung"
                  :classFormGroup="'mb-3'"
                  :labelPosition="'right'"/>
              <BaseCheckbox
                  v-model="formData.trainingContract"
                  label="Weiterbildungsvertrag"
                  :classFormGroup="'mb-3'"
                  :labelPosition="'right'"/>
              <BaseCheckbox
                  v-model="formData.dataProtection"
                  label="Datenschutz"
                  :classFormGroup="'mb-3'"
                  :labelPosition="'right'"/>
              <BaseCheckbox
                  v-model="formData.healthQuestionnaire"
                  label="Gesundheitsfragebogen"
                  :classFormGroup="'mb-3'"
                  :labelPosition="'right'"/>
              <BaseCheckbox
                  v-model="formData.instruction"
                  label="Belehrung"
                  :classFormGroup="'mb-3'"
                  :labelPosition="'right'"/>
            </div>
            <div class="col-lg-3 col-md-4 col-sm-12 mb-4">
              <BaseCheckbox
                  v-model="formData.finalReportSent"
                  label="Abschlussbericht"
                  :labelPosition="'right'"
                  @update:modelValue="onFinalReportSentChange"/>
              <div class="pr-5">
                <Datepicker
                    v-model="formData.finalReportSendingDate"
                    :required="true"
                    label="Datum"
                    position="down"
                    :disabled="isFinalReportSendingDateDisabled"
                    :validation="v$?.finalReportSendingDate"
                    :messages="v$?.finalReportSendingDate?.required?.$message"/>
              </div>
            </div>
            <div class="col-lg-3 col-md-4 col-sm-12 mb-4">
              <BaseToggle
                  v-model="formData.moreDocuments"
                  @update:modelValue="onMoreDocumentsChange"
                  label="Unterschriftspflichtig"/>
              <BaseCheckbox
                  v-model="formData.otherDocuments"
                  :disabled="isOtherDocumentsDisabled"
                  label="Abtretungserklärung"
                  :labelPosition="'right'"/>
            </div>
          </div>
          <div class="d-flex justify-content-center w-100 mt-2">
            <SaveButton
                :label="$t('general.button.save')"
                :loading="isSubmitting"/>
          </div>
        </form>
      </div>
    </div>
  </PageTemplate>
</template>

<script setup>
import portalGroupCoachingService from '@/api/Portal/GroupCoaching';
import unemployedGroupCoachingService from '@/api/Unemployed/GroupCoaching';
import BaseCheckbox from '@/components/base/BaseCheckbox.vue';
import BaseToggle from '@/components/base/BaseToggle.vue';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import { requiredRule } from '@/shared/validators/validationRules';
import { piniaUserStore } from '@/store/piniaUserStore';
import CoachingOverviewData from '@/views/App/Portal/GroupCoaching/CoachingOverviewData.vue';
import Menu from '@/views/App/Portal/GroupCoaching/Menu.vue';
import useVuelidate from '@vuelidate/core';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import SaveButton from '@/components/button/SaveButton.vue';

const piniaUserState                   = piniaUserStore();
const piniaCurrentUser                 = computed(() => piniaUserState.currentUser.user);
const { t }                            = useI18n();
const route                            = useRoute();
const coachingData                     = ref('');
const isLoaded                         = ref(false);
const isSubmitting                     = ref(false);
const isFinalReportSendingDateDisabled = ref(true);
const isOtherDocumentsDisabled         = ref(true);
const coachingId                       = +route.params.id;

const formData = ref({
  coachingId: null,
  selectionInterview: '',
  houseRule: '',
  trainingContract: '',
  dataProtection: '',
  healthQuestionnaire: '',
  instruction: '',
  finalReportSent: '',
  finalReportSendingDate: '',
  moreDocuments: '',
  otherDocuments: '',
});

const rules = ref({
  finalReportSendingDate: '',
});

const computedRules = computed(() => rules.value);
const v$            = useVuelidate(computedRules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    formData.value.coachingId = +coachingId;
    await portalGroupCoachingService.submitIntroductoryDocuments(formData.value);

    await getDocumentsData();
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getGroupCoachingData() {
  try {
    CloseToast();
    const { data } = await unemployedGroupCoachingService.getGroupCoaching(+coachingId);

    coachingData.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getDocumentsData() {
  try {
    const { data } = await portalGroupCoachingService.getIntroductoryDocuments(+coachingId);

    formData.value = { ...data.data };
  } catch (error) {
    await ErrorToast(error);
  }
}

function onFinalReportSentChange(newValue) {
  formData.value.finalReportSendingDate = '';
  if (newValue === true) {
    isFinalReportSendingDateDisabled.value = false;
    rules.value                          = {
      ...rules.value,
      finalReportSendingDate: requiredRule(t),
    };
  } else {
    isFinalReportSendingDateDisabled.value = true;
    rules.value                          = {
      ...rules.value,
      finalReportSendingDate: '',
    };
  }
}

function onMoreDocumentsChange(newValue) {
  formData.value.otherDocuments = false;
  if (newValue === true) {
    isOtherDocumentsDisabled.value = false;
  } else {
    isOtherDocumentsDisabled.value = true;
  }
}

onMounted(async () => {
  await Promise.all([
    getGroupCoachingData(),
    getDocumentsData(),
  ]);

  isLoaded.value = true;
});
</script>
<style scoped>

</style>